import { importLibrary, setOptions } from "@googlemaps/js-api-loader";

import config from "config";

export type AddressValidationLib = {
  AddressValidation: {
    fetchAddressValidation(request: {
      address: {
        regionCode: string;
        addressLines: string[];
        locality?: string;
        administrativeArea?: string;
        postalCode?: string;
      };
    }): Promise<{
      verdict?: { possibleNextAction?: unknown };
      address?: { missingComponentTypes?: string[] };
    }>;
  };
};

// setOptions only takes effect before the first library loads, so it runs here
// at module scope: this module is the single entry point for Maps loading.
setOptions({ key: config.googleMapsApiKey });

let mapsPromise: Promise<void> | null = null;

/**
 * Loads the Google Maps JS API (with the Places library) exactly once.
 * All Maps consumers must go through this so the API is configured once and
 * the `google.maps.places` globals are ready before they are touched.
 */
export function loadGoogleMaps(): Promise<void> {
  if (!mapsPromise) {
    const attempt = importLibrary("places").then(() => undefined);
    // A failed load must not poison the session: drop the memo so a later
    // call can retry (callers still receive this attempt's rejection).
    attempt.catch(() => {
      mapsPromise = null;
    });
    mapsPromise = attempt;
  }
  return mapsPromise;
}

let addressValidationPromise: Promise<AddressValidationLib> | null = null;

export function loadAddressValidationLib(): Promise<AddressValidationLib> {
  if (!addressValidationPromise) {
    const attempt = loadGoogleMaps().then(
      () =>
        importLibrary(
          "addressValidation",
        ) as unknown as Promise<AddressValidationLib>,
    );
    attempt.catch(() => {
      addressValidationPromise = null;
    });
    addressValidationPromise = attempt;
  }
  return addressValidationPromise;
}
